import type { MotionVariants } from "@vueuse/motion"

export type SpringPreset = "snappy" | "bouncy" | "gentle" | "slow" | "linear"

export type SpringConfig = {
  type: "spring"
  stiffness: number
  damping: number
  mass: number
}

export type TweenConfig = {
  type: "tween"
  duration: number
  ease: string
}

export type AnimConfig = SpringConfig | TweenConfig

const presets: Record<SpringPreset, AnimConfig> = {
  snappy: { type: "spring", stiffness: 400, damping: 28, mass: 1 },
  bouncy: { type: "spring", stiffness: 350, damping: 12, mass: 1 },
  gentle: { type: "spring", stiffness: 150, damping: 20, mass: 1 },
  slow: { type: "spring", stiffness: 80, damping: 18, mass: 1 },
  linear: { type: "tween", duration: 300, ease: "ease-in-out" },
}

export function useSpring(preset: SpringPreset = "snappy") {
  const config = presets[preset]

  function fade(): MotionVariants<"initial" | "enter" | "leave"> {
    return {
      initial: { opacity: 0 },
      enter: { opacity: 1, transition: config },
      leave: { opacity: 0, transition: config },
    }
  }

  function slideUp(
    distance = 16,
  ): MotionVariants<"initial" | "enter" | "leave"> {
    return {
      initial: { opacity: 0, y: distance },
      enter: { opacity: 1, y: 0, transition: config },
      leave: { opacity: 0, y: distance, transition: config },
    }
  }

  function scale(from = 0.95): MotionVariants<"initial" | "enter" | "leave"> {
    return {
      initial: { opacity: 0, scale: from },
      enter: { opacity: 1, scale: 1, transition: config },
      leave: { opacity: 0, scale: from, transition: config },
    }
  }

  return { config, fade, slideUp, scale }
}
